export enum TicketSource {
	SECURITY = 'security',
	PERMISSIONS = 'permissions',
	IDM_ACCESS = 'idm_access',
	POWER_AUTOMATE = 'power_automate',
}

export enum TicketSeverity {
	CRITICAL = 'critical',
	HIGH = 'high',
	MEDIUM = 'medium',
	LOW = 'low',
}

/**
 * The source-of-truth ticketing system this ticket lives in. Stored
 * in `tickets.external_system` (text column with default `'productive'`)
 * — every TypeScript reader / writer references the enum, never the
 * literal. Adding a new external system = one new enum value + a
 * matching adapter; the schema doesn't change.
 */
export enum ExternalTicketSystem {
	PRODUCTIVE = 'productive',
	// Future: JIRA = 'jira', LINEAR = 'linear', …
}

/**
 * Discriminator for `ProductiveAdapter.createTask`'s discriminated-union
 * return shape. Drives the post-create branching in `TicketService`:
 *   - `SUCCESS` → persist `external_ticket_id` + URL, emit
 *     `TICKET_CREATED`.
 *   - `TERMINAL_FAILURE` → emit `TICKET_REQUEST_FAILED` (won't succeed
 *     on retry).
 *   - `RETRYABLE_FAILURE` → silent; row sits in retry-pending state
 *     for the next poller cycle.
 */
export enum ExternalTicketCreateResultKind {
	SUCCESS = 'success',
	TERMINAL_FAILURE = 'terminal_failure',
	RETRYABLE_FAILURE = 'retryable_failure',
}

/**
 * Discriminator for the field-diff output produced by the polling
 * Lambda. Drives event-emission routing in the poller handler. Per
 * CLAUDE.md "Use Enums Instead of String Literals", this stays an
 * enum even though it's only consumed inside `ticket-service` —
 * discriminated-union `kind` literals are exactly the trap the rule
 * targets.
 *
 *   - `CLOSED` — `closed_at` flipped from null to a timestamp. Emit
 *     `TICKET_CLOSED` (NOT `TICKET_STATUS_CHANGED`; closes are
 *     terminal and the events are mutually exclusive per spec).
 *   - `STATUS_CHANGED` — `status_id` differs from the last-seen
 *     value AND the task is still open. Emit `TICKET_STATUS_CHANGED`.
 *   - `OTHER_CHANGE` — any non-status field delta (assignee, due
 *     date, priority, …). One per changed field. Emit
 *     `TICKET_OTHER_CHANGE`.
 */
export enum TicketPollDiffKind {
	CLOSED = 'closed',
	STATUS_CHANGED = 'status_changed',
	OTHER_CHANGE = 'other_change',
}

/**
 * `source` is the routing key the Productive adapter looks up to find a
 * project + task list. The DB column is `text`, and request-center tickets
 * use prefixed strings like `request-center.<request-type-slug>` that are
 * NOT in the `TicketSource` enum (the enum only models the legacy IDM /
 * security / power-automate sources). Type as a union so callers can pass
 * either an enum value (typo-safe for the legacy sources) or a free-form
 * string (for the request-center prefix family) without an `as unknown as`
 * cast at every callsite.
 */
export interface TicketInput {
	source: TicketSource | (string & {})
	dedupeKey: string
	severity: TicketSeverity
	title: string
	description: string
	metadata?: Record<string, unknown>
	/** Spec 3 — link the ticket to its parent Request Center request
	 *  when one applies. IDM / security / webhook tickets leave it
	 *  null. Drives `<TicketsSection requestId />` + the request-center
	 *  audit timeline + the `TICKET_CLOSED` request-completion
	 *  aggregator. */
	requestId?: string | null
}

export enum TicketUpsertResult {
	CREATED = 'created',
	UPDATED = 'updated',
}

export enum TicketStatus {
	OPEN = 'open',
	CLOSED = 'closed',
}

export interface TicketUpsertResponse {
	result: TicketUpsertResult
}

/**
 * Wire shape of a ticket row — backs the entity's `.toDTO()` and the
 * frontend `<TicketsSection>` (Spec 3). Optional fields mirror the
 * nullable DB columns; producers populate them when an external
 * system has been notified (`externalTicketId` etc.) and the poller
 * has run at least once (`lastSyncedAt`, `closedAt`).
 *
 * Per ADR-0003, this is the entity-owned DTO contract. Service layer
 * returns `Ticket` entities; controller calls `.toDTO()` before
 * passing to `_200`.
 */
export interface ITicket {
	ticketId: string
	source: string
	dedupeKey: string
	status: TicketStatus
	severity: TicketSeverity
	title: string
	description: string
	externalSystem: string
	externalTicketId: string | null
	externalTicketUrl: string | null
	requestId: string | null
	metadata: Record<string, unknown> | null
	lastSeenAt: Date
	lastSyncedAt: Date | null
	closedAt: Date | null
	createdAt: Date
	updatedAt: Date
}
